import { Link, Box, Flex, Image, IconButton, theme } from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { MouseEventHandler, ReactNode } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { isBrowser } from 'utils/helpers';
import { scrollYToTargetAdjusted } from 'utils/helpers/scrollHelpers';
import { useUserAgent } from 'utils/hooks';
import { ImageType } from 'utils/types';

export type NavigationTileProps = {
  /**
   * id for the card
   */
  id?: string;
  /**
   * Background color for the card
   */
  backgroundColor?: string;
  /**
   * anchor slot for the card. It should use the Button component from the UI Kit.
   */
  anchor?: string;
  /**
   * buttonLabel slot for the card. It should use the Button component from the UI Kit.
   */
  buttonLabel?: string;
  /**
   * Image slot for the card. Prefer the Gatsby Image component.
   */
  desktopImage?: ImageType;
  mobileImage?: ImageType;
  /**
   * Children to be passed inside the component, Generally the product name and a short description
   */
  children: ReactNode | ReactNode[];
  /**
   * Optional flag to capture the onClick event on the card
   */
  handleOnClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  /**
   * Optional icon name for the anchor button
   */
  iconName?: string;
  /**
   * Optional background color for the icon button
   */
  iconBackgroundColor?: string;
};

const activeStyles = { textDecoration: 'none' };

const NavigationTile = ({
  anchor,
  backgroundColor,
  buttonLabel,
  children,
  desktopImage,
  handleOnClick,
  iconName,
  mobileImage,
}: NavigationTileProps) => {
  const { areAppleDevicesPresent } = useUserAgent();
  const trackClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        label: buttonLabel,
        sectionId: anchor,
        component: 'NavigationTile',
      },
    });
  };

  const onClick = (e: any) => {
    if (anchor?.charAt(0) === '#') {
      e.preventDefault();
      scrollYToTargetAdjusted({
        id: anchor,
        areAppleDevicesPresent,
        topOffset: 130,
      });
    }
    trackClick();
    handleOnClick && handleOnClick(e);
  };

  const touchStyles = {
    '&:hover': {
      button: {
        background:
          isBrowser() && 'ontouchstart' in window
            ? 'tints.white'
            : 'viridian.wash',
      },
    },
  };

  const hasImage = desktopImage || mobileImage;

  return (
    <Flex
      _hover={activeStyles}
      as={Link}
      backgroundColor={backgroundColor || 'tints.lightCream'}
      borderRadius="xl"
      flexDirection={hasImage ? 'column' : 'row'}
      href={anchor}
      minHeight={hasImage ? ['112px', null, '128px'] : '100%'}
      minWidth={['135px', null, 'unset']}
      onClick={onClick}
      overflow="hidden"
      padding={4}
      position={'relative'}
      sx={touchStyles}
    >
      <Flex flex={1} alignItems="center">
        {children}
      </Flex>
      <Flex
        alignItems="flex-end"
        flex={hasImage ? 3 : 1}
        justifyContent={hasImage ? 'flex-start' : 'flex-end'}
      >
        {anchor && (
          <IconButton
            _hover={activeStyles}
            icon={
              <DynamicIcon
                icon={(iconName as IconKey) || 'ArrowDown'}
                fontSize={theme.fontSizes.lg}
              />
            }
            aria-label={buttonLabel || `button ${iconName} to ${anchor}`}
            color={theme.colors.tints.darkGrey}
            colorScheme="whiteCS"
            maxWidth={'32px'}
            padding="2"
            size="sm"
            variant="primary"
            data-testid={`navigation-tile-cta`}
          />
        )}
      </Flex>

      {mobileImage && (
        <Box
          bottom="0"
          position="absolute"
          right="0"
          display={['block', 'block', 'none']}
        >
          <Image
            as={GatsbyImage}
            alt={mobileImage.description}
            image={mobileImage.gatsbyImageData}
            width="80px"
          />
        </Box>
      )}
      {desktopImage && (
        <Box
          bottom="0"
          position="absolute"
          right="0"
          display={['none', null, 'block']}
        >
          <Image
            alt={desktopImage.description}
            as={GatsbyImage}
            image={desktopImage.gatsbyImageData}
            width="120px"
          />
        </Box>
      )}
    </Flex>
  );
};

export default NavigationTile;
